/**
 * Build-time addresses for this bundle.
 *
 * `VITE_*` is inlined by Vite. Web production keeps an empty API base so
 * `/api` is same-origin (Caddy proxies it). Native shells point at the hosted
 * API. The `/api` prefix itself lives here — and only here — so the ESLint
 * ban on that literal elsewhere can be a hard build failure.
 */
const API_PREFIX = '/api';

export function apiBase(): string {
  return import.meta.env.VITE_API_BASE ?? '';
}

export function publicBase(): string {
  return import.meta.env.VITE_PUBLIC_BASE ?? '';
}

export function clientId(): string {
  return import.meta.env.VITE_WORKI_CLIENT || 'web';
}

/** Bearer token path: native shells, or the foreign-origin spike (`VITE_NATIVE`). */
export function isNative(): boolean {
  return import.meta.env.VITE_NATIVE === 'true' || clientId() !== 'web';
}

/**
 * A build distributed through Google Play.
 *
 * Deliberately narrower than `isNative()`, which is also true for Electron: the
 * rule this answers is Play's, and it binds the Android package alone. Google
 * requires its own billing for a subscription bought inside an Android app and
 * forbids sending the customer to an outside payment page instead, so this
 * build sells nothing. The desktop shell is native by the same flag and is not
 * distributed by Google, so it keeps its checkout.
 */
export function isPlayBuild(): boolean {
  return clientId() === 'android';
}

/**
 * Absolute URL for an API path.
 *
 * `path` is the resource (`/quotes`) or already prefixed (`/api/quotes`).
 */
export function apiUrl(path: string): string {
  const resource = path.startsWith(API_PREFIX) ? path.slice(API_PREFIX.length) : path;
  const normalised = resource.startsWith('/') ? resource : `/${resource}`;
  return `${apiBase()}${API_PREFIX}${normalised}`;
}

/** Customer-facing origin for public links. Session value wins when the API sent one. */
export function publicOrigin(fromSession?: string | null): string {
  const session = fromSession?.replace(/\/$/, '');
  if (session) return session;
  const configured = publicBase().replace(/\/$/, '');
  if (configured) return configured;
  return window.location.origin;
}
